import React from 'react';
import { Grid, Box, Typography, Paper } from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
} from 'recharts';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { StatCard } from './StatCard';

interface LearningProgressDashboardProps {
    data: any;
}

const formatTime = (seconds: number): string => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

export const LearningProgressDashboard: React.FC<LearningProgressDashboardProps> = ({ data }) => {
    if (!data) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="textSecondary">Keine Daten verfügbar</Typography>
            </Box>
        );
    }

    const { summary, trends, courseStats } = data;

    return (
        <Box sx={{ px: 2 }}>
            {/* Hinweis zum Zeitraum-Filter (#291/#159: Heike rechnete mit allen Einschreibungen). */}
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                Gezählt werden alle, die im gewählten Zeitraum aktiv waren: eingeschrieben, gelernt oder abgeschlossen. Abschlüsse zählen nur, wenn sie im Zeitraum liegen. Pflichtaufgaben sind enthalten.
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Einschreibungen"
                        value={summary?.total_enrollments || 0}
                        info="Alle, die im gewählten Zeitraum aktiv waren: eingeschrieben, gelernt oder abgeschlossen."
                        icon={<SchoolIcon sx={{ fontSize: 48 }} />}
                        color="primary"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Abgeschlossen"
                        value={summary?.completed || 0}
                        subtitle={`${(((parseFloat(summary?.completed) || 0) / (parseFloat(summary?.total_enrollments) || 1)) * 100).toFixed(1)}% Abschlussrate`}
                        info="Abschlüsse mit Abschlussdatum im gewählten Zeitraum, auch bei älterer Einschreibung. Abschlussrate = Abschlüsse / Teilnehmende."
                        icon={<CheckCircleIcon sx={{ fontSize: 48 }} />}
                        color="success"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Durchschn. Fortschritt"
                        value={`${parseFloat(summary?.avg_progress || 0).toFixed(1)}%`}
                        info="Durchschnitt über alle, die im gewählten Zeitraum aktiv waren."
                        icon={<TrendingUpIcon sx={{ fontSize: 48 }} />}
                        color="info"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Gesamte Lernzeit"
                        value={formatTime(summary?.total_time_seconds || 0)}
                        subtitle={`Durchschn. pro Einschreibung: ${formatTime(summary?.avg_time_seconds || 0)}`}
                        info="Summe aller im Kursplayer gemessenen Lernzeiten. Wird automatisch beim Bearbeiten von Lektionen erfasst."
                        icon={<AccessTimeIcon sx={{ fontSize: 48 }} />}
                        color="warning"
                    />
                </Grid>
            </Grid>

            {/* Trend Chart */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Einschreibungen & Abschlüsse Trend
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trends || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                            type="monotone"
                            dataKey="enrollments"
                            name="Einschreibungen"
                            stroke="#1976d2"
                            strokeWidth={2}
                        />
                        <Line
                            type="monotone"
                            dataKey="completions"
                            name="Abschlüsse"
                            stroke="#4caf50"
                            strokeWidth={2}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Paper>

            {/* Course Statistics — Top 10, mit anpassbarer Hoehe + Titel-Trunkierung bei zu langem Namen. */}
            {courseStats && courseStats.length > 0 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Top Kurse nach Einschreibungen
                        {courseStats.length > 10 && (
                            <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                (von {courseStats.length} Kursen — vollstaendig im Tab "Kurs-Details")
                            </Typography>
                        )}
                    </Typography>
                    <ResponsiveContainer width="100%" height={Math.max(300, courseStats.slice(0, 10).length * 40)}>
                        <BarChart data={courseStats.slice(0, 10).map((c: any) => ({
                            ...c,
                            // Lange Titel kuerzen, sonst kollidieren sie mit der X-Achse
                            course_title_short: c.course_title?.length > 40
                                ? c.course_title.substring(0, 38) + '…'
                                : c.course_title,
                        }))} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis dataKey="course_title_short" type="category" width={260} tick={{ fontSize: 12 }} interval={0} />
                            <Tooltip
                                labelFormatter={(_, payload) => payload?.[0]?.payload?.course_title || ''}
                            />
                            <Legend />
                            <Bar dataKey="enrollments" name="Einschreibungen" fill="#1976d2" />
                            <Bar dataKey="completions" name="Abschlüsse" fill="#4caf50" />
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            )}
        </Box>
    );
};

export default LearningProgressDashboard;
